import { useQuery } from "@tanstack/react-query";
import { Api } from "../../../services/interceptor";
import { QUERY_KEY } from "../../../services/queryEndPoint";
import { LIVE_MATCH_POLL_MS } from "./helpers";
import type { MatchProgression } from "../result/derive";

/** One delivery from GET /match/{id}/live-stats. `over` is 1-based; null fields mean the scorer did not record them. */
export type LiveBall = {
  ballId: number;
  over: number;
  ball: number;
  runs: number;
  /** NONE when the ball carried no extra. */
  extraType: string | null;
  extraRuns: number;
  legal: boolean;
  batsmanId: string | null;
  batsman: string;
  batsmanBattingStyle: string | null;
  bowlerId: string | null;
  bowler: string;
  nonStrikerId: string | null;
  wicketType: string | null;
  dismissedId: string | null;
  shotType: string | null;
  sector: string | null;
  line: string | null;
  length: string | null;
};
export type LiveStatsInning = { inningId: string; inningNumber: number | null; battingTeamId: string | null; balls: LiveBall[] };
/** outcome: W, L, T (tie), D (draw) or NR (no result / abandoned). Newest first, this tournament only. */
export type FormResult = { matchId: string; date: string | null; outcome: string; opponentId: string | null; opponentName: string | null };
export type TeamForm = { teamId: string; teamName: string; results: FormResult[] };
export type LiveStats = { matchId: string; innings: LiveStatsInning[]; recentForm: TeamForm[] };

/**
 * Ball-level detail for the Live tab's current-batters card and the Live stats tab. `inningId` limits the payload to one
 * innings; pass it for multi-day formats, where all innings together are large.
 */
export function useLiveStats(matchId: string, opts: { enabled: boolean; live: boolean; inningId?: string }) {
  return useQuery<LiveStats>({
    queryKey: [QUERY_KEY.getMatchInfo, matchId, "live-stats", opts.inningId ?? "all"],
    queryFn: async () => (await Api.get(`${QUERY_KEY.getMatchInfo}/${matchId}/live-stats`, { params: opts.inningId ? { inningId: opts.inningId } : undefined })).data,
    enabled: opts.enabled && !!matchId,
    staleTime: opts.live ? LIVE_MATCH_POLL_MS : 60_000,
    refetchInterval: opts.live ? LIVE_MATCH_POLL_MS : false,
  });
}

/** Over-by-over runs and wickets for the Manhattan, run-rate and worm charts and the phase table. */
export function useProgression(matchId: string, opts: { enabled: boolean; live: boolean }) {
  return useQuery<MatchProgression>({
    queryKey: [QUERY_KEY.getMatchInfo, matchId, "progression"],
    queryFn: async () => (await Api.get(`${QUERY_KEY.getMatchInfo}/${matchId}/progression`)).data,
    enabled: opts.enabled && !!matchId,
    staleTime: opts.live ? LIVE_MATCH_POLL_MS : 60_000,
    refetchInterval: opts.live ? LIVE_MATCH_POLL_MS : false,
  });
}
